// frontend/src/modules/public/pages/HomePublica.jsx
import LeadRapidoForm from '../components/LeadRapidoForm';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaBuilding, FaUsers, FaChartLine, FaCheckCircle, 
  FaMoneyBillWave, FaFileAlt, FaLock, FaArrowRight,
  FaWhatsapp, FaEnvelope, FaPhone 
} from 'react-icons/fa';

function HomePublica() {
  const navigate = useNavigate();

  const styles = {
    container: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      color: '#212529',
      overflowX: 'hidden'
    },
    // ============================================
    // HEADER
    // ============================================
    header: {
      background: '#1a237e',
      padding: '15px 40px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'sticky',
      top: 0,
      zIndex: 100,
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
    },
    logo: {
      color: 'white',
      fontSize: '22px',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      cursor: 'pointer'
    },
    navButtons: {
      display: 'flex',
      gap: '15px',
      alignItems: 'center'
    },
    navLink: {
      color: 'white',
      textDecoration: 'none',
      fontSize: '14px',
      cursor: 'pointer',
      padding: '8px 16px',
      borderRadius: '4px',
      transition: 'background 0.2s'
    },
    loginButton: {
      background: '#4a90e2',
      color: 'white',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    // ============================================
    // HERO
    // ============================================
    hero: {
      background: 'linear-gradient(135deg, #1a237e 0%, #4a90e2 100%)',
      color: 'white',
      padding: '80px 40px',
      textAlign: 'center'
    },
    heroTitle: {
      fontSize: 'clamp(28px, 4vw, 48px)',
      fontWeight: 'bold',
      marginBottom: '20px',
      lineHeight: '1.2'
    },
    heroSubtitle: {
      fontSize: 'clamp(16px, 2vw, 20px)',
      opacity: 0.95,
      maxWidth: '700px',
      margin: '0 auto 40px',
      lineHeight: '1.6'
    },
    heroButtons: {
      display: 'flex',
      gap: '15px',
      justifyContent: 'center',
      flexWrap: 'wrap'
    },
    primaryButton: {
      background: '#28a745',
      color: 'white',
      border: 'none',
      padding: '15px 35px',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      boxShadow: '0 4px 15px rgba(40,167,69,0.3)',
      transition: 'transform 0.2s'
    },
    secondaryButton: {
      background: 'transparent',
      color: 'white',
      border: '2px solid white',
      padding: '15px 35px',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    // ============================================
    // STATS
    // ============================================
    statsSection: {
      display: 'flex',
      justifyContent: 'center',
      gap: '60px',
      padding: '40px 20px',
      background: '#f8f9fa',
      flexWrap: 'wrap'
    },
    statItem: {
      textAlign: 'center'
    },
    statNumber: {
      fontSize: '36px',
      fontWeight: 'bold',
      color: '#1a237e'
    },
    statLabel: {
      fontSize: '14px',
      color: '#6c757d',
      marginTop: '5px'
    },
    // ============================================
    // FEATURES
    // ============================================
    section: {
      padding: '80px 40px',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    sectionTitle: {
      fontSize: '32px',
      fontWeight: 'bold',
      textAlign: 'center',
      marginBottom: '15px',
      color: '#1a237e'
    },
    sectionSubtitle: {
      fontSize: '16px',
      color: '#6c757d',
      textAlign: 'center',
      marginBottom: '50px',
      maxWidth: '700px',
      margin: '0 auto 50px'
    },
    featuresGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '30px'
    },
    featureCard: {
      background: 'white',
      padding: '30px',
      borderRadius: '12px',
      boxShadow: '0 2px 15px rgba(0,0,0,0.08)',
      transition: 'transform 0.3s, box-shadow 0.3s',
      border: '1px solid #f0f0f0'
    },
    featureIcon: {
      fontSize: '36px',
      color: '#4a90e2',
      marginBottom: '20px'
    },
    featureTitle: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '10px',
      color: '#212529'
    },
    featureDescription: {
      fontSize: '14px',
      color: '#6c757d',
      lineHeight: '1.6'
    },
    // ============================================
    // CTA
    // ============================================
    cta: {
      background: 'linear-gradient(135deg, #1a237e 0%, #4a90e2 100%)',
      color: 'white',
      padding: '80px 40px',
      textAlign: 'center'
    },
    ctaTitle: {
      fontSize: '32px',
      fontWeight: 'bold',
      marginBottom: '20px'
    },
    ctaSubtitle: {
      fontSize: '18px',
      opacity: 0.95,
      marginBottom: '40px',
      maxWidth: '600px',
      margin: '0 auto 40px'
    },
    // ============================================
    // FOOTER
    // ============================================
    footer: {
      background: '#0d1b4c',
      color: 'white',
      padding: '40px 20px',
      textAlign: 'center'
    },
    footerLinks: {
      display: 'flex',
      justifyContent: 'center',
      gap: '30px',
      marginBottom: '20px',
      flexWrap: 'wrap'
    },
    footerLink: {
      color: '#adb5bd',
      textDecoration: 'none',
      fontSize: '14px',
      cursor: 'pointer'
    },
    footerText: {
      color: '#6c757d',
      fontSize: '13px'
    }
  };

  return (
    <div style={styles.container}>
      {/* HEADER */}
      <header style={styles.header}>
        <div style={styles.logo}>
          📊 Contábil
        </div>
       <nav style={styles.navButtons}>
  <a 
    style={styles.navLink} 
    onClick={() => document.getElementById('servicos')?.scrollIntoView({ behavior: 'smooth' })}
  >
    Serviços
  </a>
  <a 
    style={styles.navLink}
    onClick={() => document.getElementById('contato')?.scrollIntoView({ behavior: 'smooth' })}
  >
    Contato
  </a>
  <button 
    style={styles.loginButton}
    onClick={() => navigate('/login')}
  >
    <FaLock /> Acessar Sistema
  </button>
</nav>
      </header>

      {/* HERO */}
      <section style={styles.hero}>
        <h1 style={styles.heroTitle}>
          Gestão Contábil<br />
          Simples e Completa
        </h1>
        <p style={styles.heroSubtitle}>
          Gerencie folha de pagamento, impostos e obrigações fiscais da sua empresa 
          em uma única plataforma. Simples, rápida e segura.
        </p>
        <div style={styles.heroButtons}>
          <button 
            style={styles.primaryButton}
            onClick={() => navigate('/abrir-empresa')}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <FaBuilding /> Abrir Empresa
          </button>
          <button 
            style={styles.secondaryButton}
            onClick={() => navigate('/encerrar-empresa')}
          >
            🚪 Encerrar Empresa
          </button>
        </div>
      </section>

      {/* STATS */}
      <section style={styles.statsSection}>
        <div style={styles.statItem}>
          <div style={styles.statNumber}>500+</div>
          <div style={styles.statLabel}>Empresas atendidas</div>
        </div>
        <div style={styles.statItem}>
          <div style={styles.statNumber}>10K+</div>
          <div style={styles.statLabel}>Funcionários gerenciados</div>
        </div>
        <div style={styles.statItem}>
          <div style={styles.statNumber}>99.9%</div>
          <div style={styles.statLabel}>Disponibilidade</div>
        </div>
      </section>
	  
	  {/* ============================================ */}
{/* SEÇÃO DE CAPTURA DE LEADS                    */}
{/* ============================================ */}
<section id="contato" style={{
  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
  padding: '80px 40px'
}}>
  <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
    
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '40px',
      alignItems: 'center'
    }}>
      
      {/* Texto à esquerda */}
      <div>
        <span style={{
          background: '#28a745',
          color: 'white',
          padding: '5px 15px',
          borderRadius: '20px',
          fontSize: '13px',
          fontWeight: '600',
          display: 'inline-block',
          marginBottom: '15px'
        }}>
          💼 Contabilidade Online
        </span>
        
        <h2 style={{
          fontSize: 'clamp(24px, 3vw, 36px)',
          fontWeight: 'bold',
          color: '#1a237e',
          marginBottom: '20px',
          lineHeight: '1.3'
        }}>
          Quer trocar de contador ou abrir sua empresa?
        </h2>
        
        <p style={{
          fontSize: '16px',
          color: '#6c757d',
          lineHeight: '1.7',
          marginBottom: '25px'
        }}>
          Receba uma <strong>análise gratuita</strong> da sua situação contábil.
          Nossa equipe entrará em contato em até <strong>24 horas</strong> para
          entender sua necessidade.
        </p>

        {/* Lista de benefícios */}
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#28a745', fontSize: '18px' }}>✓</span>
            <span style={{ color: '#495057' }}>Atendimento 100% online</span>
          </li>
          <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#28a745', fontSize: '18px' }}>✓</span>
            <span style={{ color: '#495057' }}>Sem burocracia na migração</span>
          </li>
          <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#28a745', fontSize: '18px' }}>✓</span>
            <span style={{ color: '#495057' }}>Preço justo e transparente</span>
          </li>
          <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#28a745', fontSize: '18px' }}>✓</span>
            <span style={{ color: '#495057' }}>Suporte por WhatsApp</span>
          </li>
        </ul>
      </div>

      {/* Formulário à direita */}
      <div style={{
        background: 'white',
        padding: '35px',
        borderRadius: '16px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{
          fontSize: '22px',
          fontWeight: 'bold',
          color: '#1a237e',
          marginBottom: '10px',
          marginTop: 0
        }}>
          📞 Solicite um contato
        </h3>
        <p style={{ color: '#6c757d', fontSize: '14px', marginBottom: '25px' }}>
          Preencha os dados e entraremos em contato.
        </p>

        <LeadRapidoForm />
      </div>
    </div>
  </div>
</section>

      {/* SERVIÇOS */}
      <section id="servicos" style={styles.section}>
        <h2 style={styles.sectionTitle}>Nossos Serviços</h2>
        <p style={styles.sectionSubtitle}>
          Tudo o que sua empresa precisa para estar em dia com a contabilidade
        </p>

        <div style={styles.featuresGrid}>
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}><FaBuilding /></div>
            <h3 style={styles.featureTitle}>Abertura de Empresa</h3>
            <p style={styles.featureDescription}>
              Abra sua empresa de forma rápida e descomplicada. Cuidamos de toda a 
              burocracia para você.
            </p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}><FaUsers /></div>
            <h3 style={styles.featureTitle}>Gestão de Funcionários</h3>
            <p style={styles.featureDescription}>
              Cadastre e gerencie todos os funcionários, benefícios, adiantamentos 
              e rescisões em um só lugar.
            </p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}><FaMoneyBillWave /></div>
            <h3 style={styles.featureTitle}>Folha de Pagamento</h3>
            <p style={styles.featureDescription}>
              Processe a folha automaticamente com cálculos precisos de INSS, 
              IRRF, FGTS e geração de holerites.
            </p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}><FaFileAlt /></div>
            <h3 style={styles.featureTitle}>Guias e DARF</h3>
            <p style={styles.featureDescription}>
              Gere guias de pagamento, DARF, GPS e muito mais. Tudo calculado 
              automaticamente pelo sistema.
            </p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}><FaChartLine /></div>
            <h3 style={styles.featureTitle}>Relatórios Completos</h3>
            <p style={styles.featureDescription}>
              Relatórios detalhados para tomada de decisão. Exporte em PDF e Excel 
              quando precisar.
            </p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}><FaCheckCircle /></div>
            <h3 style={styles.featureTitle}>Encerramento</h3>
            <p style={styles.featureDescription}>
              Precisa encerrar sua empresa? Cuidamos de todo o processo com 
              segurança e agilidade.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={styles.cta}>
        <h2 style={styles.ctaTitle}>Pronto para começar?</h2>
        <p style={styles.ctaSubtitle}>
          Simplifique a gestão da sua empresa hoje mesmo. Abra sua empresa ou 
          acesse o sistema.
        </p>
        <div style={styles.heroButtons}>
          <button 
            style={styles.primaryButton}
            onClick={() => navigate('/abrir-empresa')}
          >
            <FaBuilding /> Abrir Empresa
          </button>
          <button 
            style={styles.secondaryButton}
            onClick={() => navigate('/login')}
          >
            <FaLock /> Acessar Sistema
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer id="contato" style={styles.footer}>
        <div style={styles.footerLinks}>
          <a style={styles.footerLink}>📧 contato@contabil.com.br</a>
          <a style={styles.footerLink}>📱 (11) 99999-9999</a>
          <a style={styles.footerLink}>💬 WhatsApp</a>
        </div>
        <p style={styles.footerText}>
          © 2026 Contábil - Todos os direitos reservados
        </p>
      </footer>
    </div>
  );
}

export default HomePublica;